import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'
import posthog from 'posthog-js'
import { FiChevronLeft } from 'react-icons/fi'
import Header from 'components/headers/addLoan'
import { PulseCard, SectionError } from 'components/states'
import { getMyDeposits } from 'services/deposits'
import { fundName, longJalali, toman } from 'utilities/helper/format'
import { STATE_LABELS } from '../detail'

/**
 * «درخواست‌های برداشت» — the member's own open requests, one row per fund.
 *
 * Where «پیگیری درخواست‌های برداشت» lands when there is more than one; with a
 * single request it goes straight to that request instead. Read off
 * `my-deposits`, the query home has already loaded, because that is where the
 * count on the button comes from — the list and the number cannot disagree.
 *
 * A request that has been paid and is waiting on the member's «تأیید دریافت»
 * comes first: it is the only row asking them to do something.
 */
export default function MyWithdrawalRequests() {
    const navigate = useNavigate()
    const { data, isLoading, isError, refetch } = useQuery(
        ['my-deposits'],
        getMyDeposits,
        { staleTime: 60_000 }
    )

    const rows = (data?.funds ?? [])
        .filter((fund) => fund.open_request)
        .sort(
            (a, b) =>
                Number(b.open_request!.state === 'wdpd') -
                    Number(a.open_request!.state === 'wdpd') ||
                a.open_request!.id - b.open_request!.id
        )

    return (
        <div className=" min-h-screen bg-canvas pb-10">
            <Header title="درخواست‌های برداشت" />

            <div className=" px-4 pt-4">
                {isLoading && <PulseCard />}
                {isError && <SectionError onRetry={() => refetch()} />}

                {data && rows.length === 0 && (
                    <p className=" rounded-2xl border border-stroke bg-white p-6 text-center text-xs leading-6 text-text-secondary">
                        درخواست برداشت بازی ندارید.
                    </p>
                )}

                <div className=" flex flex-col gap-3">
                    {rows.map((fund) => {
                        const request = fund.open_request!
                        return (
                            <button
                                key={request.id}
                                type="button"
                                onClick={() => {
                                    posthog.capture(
                                        'withdrawal_request_opened',
                                        { source: 'list' }
                                    )
                                    navigate(
                                        `/fund/withdraw-request/detail/${request.id}?fund=${fund.moneypool_id}`
                                    )
                                }}
                                className=" flex w-full items-center justify-between gap-2 rounded-2xl border border-stroke bg-white p-4 text-right"
                            >
                                <span className=" min-w-0">
                                    <span className=" block text-sm font-bold text-heading line-clamp-2 break-words">
                                        {fundName(fund.moneypool_name)}
                                    </span>
                                    <span className=" mt-1 block text-[15px] font-bold text-heading">
                                        {toman(request.amount)}
                                    </span>
                                    <span
                                        className={` mt-1 block text-[11px] ${
                                            request.state === 'wdpd'
                                                ? ' font-bold text-success-on'
                                                : ' text-warning-on'
                                        }`}
                                    >
                                        {STATE_LABELS[request.state]}
                                    </span>
                                    {request.paid_amount > 0 &&
                                        request.remaining > 0 && (
                                            <span className=" mt-[2px] block text-[11px] text-text-secondary">
                                                {toman(request.paid_amount)}{' '}
                                                پرداخت شده · باقیمانده{' '}
                                                {toman(request.remaining)}
                                            </span>
                                        )}
                                    <span className=" mt-[2px] block text-[11px] text-text-muted">
                                        ثبت‌شده در {longJalali(request.created)}
                                    </span>
                                </span>
                                <FiChevronLeft className=" shrink-0 text-text-muted" />
                            </button>
                        )
                    })}
                </div>
            </div>
        </div>
    )
}
